import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Box, TextField, MenuItem, Button, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { formInputSx } from '../styles/inputStyles';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCircleInfo, faRobot, faWarning } from '@fortawesome/free-solid-svg-icons';
import { faFacebook, faInstagram, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './SelectBotModal.css';
import './SocialConnectModal.css';
import ManualReplyPauseSetting from './ManualReplyPauseSetting';

const SelectBotModal = ({ open, onClose, onConfirm, platform, defaultBotId }) => {
  const { t } = useTranslation();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBotId, setSelectedBotId] = useState('');
  const [autoResponseMode, setAutoResponseMode] = useState('respond_all');
  const [pauseOnManualReply, setPauseOnManualReply] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (open) {
      setSelectedBotId(defaultBotId || '');
      setAutoResponseMode('respond_all');
      setPauseOnManualReply(true);
      if (defaultBotId) {
        setBots([{ id: defaultBotId }]);
        setLoading(false);
      } else fetchBots();
    }
  }, [open, defaultBotId]);

  const fetchBots = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}bots?user_id=${localStorage.getItem("user_id") || ''}&include_knowledge=False`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      const completedBots = data.filter((bot) => bot.status === 'completed');
      setBots(completedBots);
    } catch (err) {
      console.error('Error fetching bots:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedBotId) return;

    setConfirming(true);
    try {
      await onConfirm(selectedBotId, autoResponseMode, pauseOnManualReply);
    } finally {
      setConfirming(false);
    }
  };

  const getPlatformDisplayName = (platform) => {
    const platformNames = {
      facebook: 'Facebook',
      instagram: 'Instagram',
      whatsapp: 'WhatsApp',
    };
    return platformNames[platform] || platform;
  };

  const getPlatformIcon = (platform) => {
    const platformIcons = {
      facebook: faFacebook,
      instagram: faInstagram,
      whatsapp: faWhatsapp,
    };
    return platformIcons[platform?.toLowerCase()] || faFacebook;
  };

  const getPlatformColor = (platform) => {
    const platformColors = {
      facebook: '#1877f2',
      instagram: '#E4405F',
      whatsapp: '#25D366',
    };
    return platformColors[platform?.toLowerCase()] || '#5616ea';
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="social-connect-modal"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          width: isMobile ? '85%' : '90%',
          maxWidth: 480,
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        <Box className="social-connect-modal-header"
          sx={{
            flexShrink: 0,
            px: isMobile ? 2.5 : 2.75,
            pt: isMobile ? 2.5 : 2.75,
            pb: 2,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div className="select-bot-modal-header" style={{ marginBottom: 0 }}>
            <FontAwesomeIcon
              icon={getPlatformIcon(platform)}
              style={{ color: getPlatformColor(platform), fontSize: '2rem', marginRight: '12px' }}
            />
            <h3 className="select-bot-modal-title">
              {t('connector_connect_platform', { platform: getPlatformDisplayName(platform) })}
            </h3>
          </div>
        </Box>

        <Box className="social-connect-modal-body"
          sx={{
            px: isMobile ? 2.5 : 2.75,
            pt: 2,
            pb: isMobile ? 2.5 : 2.75,
            overflowY: 'auto',
          }}
        >

        {!defaultBotId && <div className="select-bot-info-box">
          <FontAwesomeIcon className="select-bot-info-icon" icon={faCircleInfo} />
          <div className="select-bot-info-content">
            <p>
              {t('connector_select_bot_info', { platform: getPlatformDisplayName(platform) })}
            </p>
          </div>
        </div>}

        {loading ? (
          <div className="select-bot-loading">
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#5616ea' }} />
          </div>
        ) : bots.length === 0 ? (
          <div className="select-bot-empty">
            <FontAwesomeIcon icon={faWarning} style={{ fontSize: '2.5rem', color: '#fac22a', marginBottom: '12px' }} />
            <p>{t('connector_no_bots_warning')}</p>
          </div>
        ) : (
          <>
            {!defaultBotId && <TextField
              select
              fullWidth
              variant="outlined"
              label={t('connector_select_a_bot')}
              value={selectedBotId}
              onChange={(e) => setSelectedBotId(e.target.value)}
              className="user-settings-input select-bot-form-control"
              sx={formInputSx}
            >
              {bots.map((bot) => (
                <MenuItem key={bot.id} value={bot.id}>
                  {bot.name || bot.domain_name || bot.id}
                </MenuItem>
              ))}
            </TextField>}

            <div className="auto-response-mode-section">
              <div className="auto-response-mode-label">{t('connector_reply_behavior')}</div>
              <RadioGroup
                value={autoResponseMode}
                onChange={(e) => setAutoResponseMode(e.target.value)}
                className="auto-response-mode-group"
              >
                <FormControlLabel
                  value="respond_all"
                  className={`auto-response-mode-option${autoResponseMode === 'respond_all' ? ' is-selected' : ''}`}
                  control={
                    <Radio
                      disableRipple
                      sx={{
                        color: '#c4c4c4',
                        '&.Mui-checked': { color: '#5616ea' },
                      }}
                    />
                  }
                  label={
                    <div className="auto-response-mode-option-text">
                      <span className="auto-response-mode-option-title">{t('connector_reply_all_title')}</span>
                      <span className="auto-response-mode-option-help">
                        {t('connector_reply_all_help')}
                      </span>
                    </div>
                  }
                />
                <FormControlLabel
                  value="opt_in"
                  className={`auto-response-mode-option${autoResponseMode === 'opt_in' ? ' is-selected' : ''}`}
                  control={
                    <Radio
                      disableRipple
                      sx={{
                        color: '#c4c4c4',
                        '&.Mui-checked': { color: '#5616ea' },
                      }}
                    />
                  }
                  label={
                    <div className="auto-response-mode-option-text">
                      <span className="auto-response-mode-option-title">{t('connector_reply_optin_title')}</span>
                      <span className="auto-response-mode-option-help">
                        {t('connector_reply_optin_help')}
                      </span>
                    </div>
                  }
                />
              </RadioGroup>
              <ManualReplyPauseSetting platform={platform} checked={pauseOnManualReply}
                onChange={setPauseOnManualReply} disabled={confirming} />
            </div>

          </>
        )}
        </Box>

            <div className="select-bot-modal-actions social-connect-modal-footer">
              <Button
                onClick={onClose}
                variant="outlined"
                disabled={confirming}
                sx={{
                  color: '#5616ea',
                  borderColor: '#5616ea',
                  '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
                }}
              >
                {t('cancel')}
              </Button>

              <Button
                onClick={handleConfirm}
                disabled={loading || bots.length === 0 || !selectedBotId || confirming}
                variant="contained"
                sx={{
                  backgroundColor: '#5616ea',
                  '&:hover': { backgroundColor: '#4a14d6' },
                  '&.Mui-disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  },
                }}
              >
                {confirming ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                    {t('connector_connecting')}
                  </>
                ) : (
                  t('connector_connect_platform', { platform: getPlatformDisplayName(platform) })
                )}
              </Button>
            </div>
      </Box>
    </Modal>
  );
};

export default SelectBotModal;
